import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Envelope,
  Users,
  ListBullets,
  Eye,
  EyeSlash,
  ShieldCheck,
  Check,
  X,
  Play,
  Stop,
  ArrowsClockwise,
  Clock,
  Pulse,
  Warning,
  TrendUp,
  Lightning,
  Trash,
} from "@phosphor-icons/react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("events");
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: "smtp.gmail.com",
    smtp_port: 587,
    smtp_email: "",
    smtp_password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [users, setUsers] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [eventLogs, setEventLogs] = useState([]);
  const [eventFilter, setEventFilter] = useState("all");
  const [schedulerStatus, setSchedulerStatus] = useState({ running: false });
  const [loading, setLoading] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [runningCheck, setRunningCheck] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [smtpRes, usersRes, logsRes, eventsRes, schedulerRes] = await Promise.all([
        api.get("/admin/smtp-config"),
        api.get("/admin/users"),
        api.get("/admin/email-logs"),
        api.get("/admin/event-logs?limit=200"),
        api.get("/admin/scheduler-status"),
      ]);

      if (smtpRes.data && smtpRes.data.smtp_email) {
        setSmtpConfig({
          smtp_host: smtpRes.data.smtp_host || "smtp.gmail.com",
          smtp_port: smtpRes.data.smtp_port || 587,
          smtp_email: smtpRes.data.smtp_email || "",
          smtp_password: "",
        });
      }
      setUsers(usersRes.data || []);
      setEmailLogs(logsRes.data || []);
      setEventLogs(eventsRes.data.logs || []);
      setSchedulerStatus(schedulerRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh event logs every 30 seconds
    const interval = setInterval(async () => {
      try {
        const eventsRes = await api.get("/admin/event-logs?limit=200");
        setEventLogs(eventsRes.data.logs || []);
      } catch (e) {
        console.error(e);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    if (!smtpConfig.smtp_email || !smtpConfig.smtp_password) {
      toast.error("Email and password are required");
      return;
    }

    setSavingSmtp(true);
    try {
      await api.post("/admin/smtp-config", smtpConfig);
      toast.success("SMTP configuration saved");
      setSmtpConfig({ ...smtpConfig, smtp_password: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save SMTP config");
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleSchedulerToggle = async () => {
    try {
      if (schedulerStatus.running) {
        await api.post("/admin/scheduler/stop");
        toast.success("Scheduler stopped");
      } else {
        await api.post("/admin/scheduler/start");
        toast.success("Scheduler started (15-minute interval)");
      }
      const res = await api.get("/admin/scheduler-status");
      setSchedulerStatus(res.data);
    } catch (e) {
      toast.error("Failed to toggle scheduler");
    }
  };

  const handleRunNow = async () => {
    setRunningCheck(true);
    try {
      await api.post("/admin/scheduler/run-now");
      toast.success("Manual check completed");
      // Refresh event logs
      const eventsRes = await api.get("/admin/event-logs?limit=200");
      setEventLogs(eventsRes.data.logs || []);
    } catch (e) {
      toast.error("Failed to run check");
    } finally {
      setRunningCheck(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm("Are you sure you want to clear all event logs?")) {
      return;
    }
    try {
      await api.delete("/admin/event-logs");
      setEventLogs([]);
      toast.success("Event logs cleared");
    } catch (e) {
      toast.error("Failed to clear logs");
    }
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case "balance_check":
        return <Pulse size={16} className="text-[#002FA7]" />;
      case "market_check":
        return <TrendUp size={16} className="text-[#00C853]" />;
      case "circuit_breaker":
        return <Warning size={16} className="text-[#FF3B30]" />;
      case "signal_detected":
        return <Lightning size={16} className="text-[#FFCC00]" />;
      case "bot_action":
        return <Play size={16} className="text-[#002FA7]" />;
      case "error":
        return <X size={16} className="text-[#FF3B30]" />;
      default:
        return <Clock size={16} className="text-[#555555]" />;
    }
  };

  const getEventBadgeStyle = (eventType) => {
    switch (eventType) {
      case "balance_check":
        return "bg-[#002FA7]/10 text-[#002FA7]";
      case "market_check":
        return "bg-[#00C853]/10 text-[#00C853]";
      case "circuit_breaker":
        return "bg-[#FF3B30]/10 text-[#FF3B30]";
      case "signal_detected":
        return "bg-[#FFCC00]/20 text-[#111111]";
      case "bot_action":
        return "bg-[#002FA7]/10 text-[#002FA7]";
      case "error":
        return "bg-[#FF3B30]/10 text-[#FF3B30]";
      default:
        return "bg-[#E5E5E5] text-[#555555]";
    }
  };

  const filteredEvents = eventFilter === "all" 
    ? eventLogs 
    : eventLogs.filter(e => e.event_type === eventFilter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F0]">
        <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      {/* Header */}
      <header className="bg-white border-b border-[#111111]/10 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-btn"
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-[#F4F4F0] rounded transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-[#002FA7]" />
              <h1 className="font-bold text-lg tracking-tight">Admin Panel</h1>
            </div>
          </div>

          {/* Scheduler Controls */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span className={`status-dot ${schedulerStatus.running ? "status-active" : "status-inactive"}`}></span>
              <span className="text-[#555555]">
                Scheduler: {schedulerStatus.running ? "Running" : "Stopped"}
              </span>
            </div>
            <button
              data-testid="run-now-btn"
              onClick={handleRunNow}
              disabled={runningCheck}
              className="px-3 py-1.5 text-sm flex items-center gap-2 border border-[#111111]/10 hover:bg-[#F4F4F0] disabled:opacity-50"
            >
              {runningCheck ? (
                <div className="w-4 h-4 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
              ) : (
                <ArrowsClockwise size={16} />
              )}
              Run Now
            </button>
            <button
              data-testid="scheduler-toggle-btn"
              onClick={handleSchedulerToggle}
              className={`px-3 py-1.5 text-sm flex items-center gap-2 ${
                schedulerStatus.running
                  ? "btn-danger"
                  : "btn-success"
              }`}
            >
              {schedulerStatus.running ? <Stop size={16} /> : <Play size={16} />}
              {schedulerStatus.running ? "Stop" : "Start"} Scheduler
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-4">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#111111]/10">
          <button
            data-testid="tab-events"
            onClick={() => setActiveTab("events")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "events"
                ? "border-[#002FA7] text-[#002FA7]"
                : "border-transparent text-[#555555] hover:text-[#111111]"
            }`}
          >
            <Pulse size={18} className="inline mr-2" />
            Event Logs ({eventLogs.length})
          </button>
          <button
            data-testid="tab-smtp"
            onClick={() => setActiveTab("smtp")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "smtp"
                ? "border-[#002FA7] text-[#002FA7]"
                : "border-transparent text-[#555555] hover:text-[#111111]"
            }`}
          >
            <Envelope size={18} className="inline mr-2" />
            SMTP Config
          </button>
          <button
            data-testid="tab-users"
            onClick={() => setActiveTab("users")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "users"
                ? "border-[#002FA7] text-[#002FA7]"
                : "border-transparent text-[#555555] hover:text-[#111111]"
            }`}
          >
            <Users size={18} className="inline mr-2" />
            Users ({users.length})
          </button>
          <button
            data-testid="tab-email-logs"
            onClick={() => setActiveTab("email-logs")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "email-logs"
                ? "border-[#002FA7] text-[#002FA7]"
                : "border-transparent text-[#555555] hover:text-[#111111]"
            }`}
          >
            <ListBullets size={18} className="inline mr-2" />
            Email Logs
          </button>
        </div>

        {/* Event Logs Tab */}
        {activeTab === "events" && (
          <div className="bg-white border border-[#111111]/10 animate-fade-in" data-testid="events-section">
            <div className="panel-header flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Pulse size={18} />
                <span className="font-medium">System Event Logs</span>
                <span className="text-xs text-[#555555]">(Auto-refreshes every 30s)</span>
              </div>
              <div className="flex items-center gap-3">
                <Select value={eventFilter} onValueChange={setEventFilter}>
                  <SelectTrigger className="w-[180px] h-8 text-sm" data-testid="event-filter">
                    <SelectValue placeholder="Filter events" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    <SelectItem value="balance_check">Balance Checks</SelectItem>
                    <SelectItem value="market_check">Market Checks</SelectItem>
                    <SelectItem value="circuit_breaker">Circuit Breaker</SelectItem>
                    <SelectItem value="signal_detected">Signals</SelectItem>
                    <SelectItem value="bot_action">Bot Actions</SelectItem>
                    <SelectItem value="error">Errors</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  data-testid="clear-logs-btn"
                  onClick={handleClearLogs}
                  className="p-2 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded"
                  title="Clear all logs"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[600px]">
              {filteredEvents.length > 0 ? (
                <div className="divide-y divide-[#111111]/10">
                  {filteredEvents.map((event) => (
                    <div 
                      key={event.id} 
                      className="p-3 hover:bg-[#F4F4F0]/50 transition-colors"
                      data-testid={`event-row-${event.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">{getEventIcon(event.event_type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 text-xs ${getEventBadgeStyle(event.event_type)}`}>
                              {event.event_type.replace("_", " ").toUpperCase()}
                            </span>
                            {event.user_email && (
                              <span className="text-xs text-[#555555] font-mono">
                                {event.user_email}
                              </span>
                            )}
                            {event.symbol && (
                              <span className="text-xs font-medium text-[#002FA7]">
                                {event.symbol}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-mono">{event.message}</p>
                          {event.details && (
                            <details className="mt-1">
                              <summary className="text-xs text-[#555555] cursor-pointer hover:text-[#111111]">
                                View details
                              </summary>
                              <pre className="mt-1 p-2 bg-[#F4F4F0] text-xs overflow-x-auto">
                                {JSON.stringify(event.details, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                        <div className="text-xs text-[#555555] whitespace-nowrap">
                          {new Date(event.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-[#555555]">
                  <Clock size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No event logs yet</p>
                  <p className="text-xs mt-1">Events will appear here when the scheduler runs</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SMTP Config Tab */}
        {activeTab === "smtp" && (
          <div className="bg-white border border-[#111111]/10 animate-fade-in" data-testid="smtp-section">
            <div className="panel-header flex items-center gap-2">
              <Envelope size={18} />
              <span className="font-medium">Gmail SMTP Configuration</span>
            </div>
            <div className="p-6">
              <p className="text-sm text-[#555555] mb-6">
                Configure Gmail SMTP to send trade notifications. You'll need to use an App Password
                for Gmail. Go to Google Account → Security → 2-Step Verification → App passwords.
              </p>

              <form onSubmit={handleSaveSmtp} className="space-y-4 max-w-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="data-label block mb-2">SMTP Host</label>
                    <Input
                      data-testid="smtp-host-input"
                      type="text"
                      value={smtpConfig.smtp_host}
                      onChange={(e) =>
                        setSmtpConfig({ ...smtpConfig, smtp_host: e.target.value })
                      }
                      className="w-full h-10 border-[#111111]/10 rounded-none font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="data-label block mb-2">SMTP Port</label>
                    <Input
                      data-testid="smtp-port-input"
                      type="number"
                      value={smtpConfig.smtp_port}
                      onChange={(e) =>
                        setSmtpConfig({ ...smtpConfig, smtp_port: parseInt(e.target.value) })
                      }
                      className="w-full h-10 border-[#111111]/10 rounded-none font-mono text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="data-label block mb-2">Gmail Address</label>
                  <Input
                    data-testid="smtp-email-input"
                    type="email"
                    value={smtpConfig.smtp_email}
                    onChange={(e) =>
                      setSmtpConfig({ ...smtpConfig, smtp_email: e.target.value })
                    }
                    placeholder="your@gmail.com"
                    className="w-full h-10 border-[#111111]/10 rounded-none font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="data-label block mb-2">App Password</label>
                  <div className="relative">
                    <Input
                      data-testid="smtp-password-input"
                      type={showPassword ? "text" : "password"}
                      value={smtpConfig.smtp_password}
                      onChange={(e) =>
                        setSmtpConfig({ ...smtpConfig, smtp_password: e.target.value })
                      }
                      placeholder="Enter app password"
                      className="w-full h-10 border-[#111111]/10 rounded-none font-mono text-sm pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555555] hover:text-[#111111]"
                    >
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-[#555555] mt-1">
                    Leave blank to keep existing password
                  </p>
                </div>

                <button
                  data-testid="save-smtp-btn"
                  type="submit"
                  disabled={savingSmtp}
                  className="px-6 h-10 btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {savingSmtp ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Save Configuration"
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="bg-white border border-[#111111]/10 animate-fade-in" data-testid="users-section">
            <div className="panel-header flex items-center gap-2">
              <Users size={18} />
              <span className="font-medium">Registered Users</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr className="bg-[#F4F4F0]">
                    <th className="text-left">Name</th>
                    <th className="text-left">Email</th>
                    <th className="text-center">Role</th>
                    <th className="text-right">Balance</th>
                    <th className="text-center">Mode</th>
                    <th className="text-left">Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td className="font-medium">{u.name}</td>
                      <td className="font-mono text-sm">{u.email}</td>
                      <td className="text-center">
                        <span
                          className={`px-2 py-0.5 text-xs ${
                            u.role === "admin"
                              ? "bg-[#002FA7]/10 text-[#002FA7]"
                              : "bg-[#E5E5E5] text-[#555555]"
                          }`}
                        >
                          {u.role.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-right font-mono">${u.balance?.toFixed(2)}</td>
                      <td className="text-center">
                        <span
                          className={`px-2 py-0.5 text-xs ${
                            u.trading_mode === "live"
                              ? "bg-[#FF3B30]/10 text-[#FF3B30]"
                              : "bg-[#00C853]/10 text-[#00C853]"
                          }`}
                        >
                          {u.trading_mode?.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-sm text-[#555555]">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="p-8 text-center text-[#555555]">
                  <p className="text-sm">No users registered yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email Logs Tab */}
        {activeTab === "email-logs" && (
          <div className="bg-white border border-[#111111]/10 animate-fade-in" data-testid="email-logs-section">
            <div className="panel-header flex items-center gap-2">
              <ListBullets size={18} />
              <span className="font-medium">Email Logs</span>
            </div>
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full table-dense">
                <thead className="sticky top-0 bg-white">
                  <tr className="bg-[#F4F4F0]">
                    <th className="text-left">Date</th>
                    <th className="text-left">To</th>
                    <th className="text-left">Subject</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.map((log) => (
                    <tr key={log.id} data-testid={`email-log-row-${log.id}`}>
                      <td className="text-sm text-[#555555]">
                        {new Date(log.sent_at).toLocaleString()}
                      </td>
                      <td className="font-mono text-sm">{log.to}</td>
                      <td className="text-sm">{log.subject}</td>
                      <td className="text-center">
                        {log.status === "sent" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[#00C853]/10 text-[#00C853]">
                            <Check size={14} />
                            SENT
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[#FF3B30]/10 text-[#FF3B30]">
                            <X size={14} />
                            FAILED
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {emailLogs.length === 0 && (
                <div className="p-8 text-center text-[#555555]">
                  <p className="text-sm">No email logs yet</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
